import { makeAutoObservable, runInAction } from 'mobx'
import { uiStore } from 'shared/store/uiStore'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { type InboxLayerStore } from 'pages/settings/pages/inboxes/pages/id/store/inboxLayerStore'
import { InboxesPath } from 'pages/settings/pages/inboxes/route/inboxesPath'
import { type IAddNumberType, resolveInitialAddNumberType } from 'pages/settings/lib/addNumberType'

export class AddNumbersStore {
  private _addNumberType: IAddNumberType = 'assign'
  private _initialLoading = true

  get addNumberType() {
    return this._addNumberType
  }

  constructor(private _inboxLayerStore: InboxLayerStore, private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  init = async () => {
    await this._entities.numbersStore.fetchUnassignedNumbers(true)
    runInAction(() => {
      this._initialLoading = false
      this._addNumberType = resolveInitialAddNumberType(
        this.unassignedNumbersCount,
        this.isGracePeriod
      )
    })
  }

  get initialLoading() {
    return this._initialLoading
  }

  get isGracePeriod() {
    return this._entities.organizationStore.isNumbersGracePeriod
  }

  get isManagerRole() {
    return this._entities.usersStore.user?.isManagerRole
  }

  get isBillingSettingsAccess() {
    return this._entities.userPermissionsStore.can_manage_billing_settings
  }

  get shouldHideAddNumbersFlow() {
    return this.isManagerRole || !this.isBillingSettingsAccess
  }

  get inboxId() {
    return this._inboxLayerStore.inboxId
  }

  get unassignedNumbers() {
    return this._entities.numbersStore.unassignedNumbersUser.filter(
      (number) => !(number.isLandline && number.status !== 'verifiedLandline')
    )
  }

  get unassignedNumbersCount() {
    return this.unassignedNumbers.length
  }

  setAddNumberType = (type: IAddNumberType) => {
    this._addNumberType = type
  }

  nextClickHandler = () => {
    const routes: Partial<Record<IAddNumberType, string>> = {
      assign: `${InboxesPath.inboxNumbersAssignNumber(this.inboxId)}`,
      buy: `${InboxesPath.inboxNumbersBuyNumber(this.inboxId)}`,
      app: `${InboxesPath.inboxNumbersAddNumberFromApp(this.inboxId)}`,
      landline: `${InboxesPath.inboxNumbersAddLandlineNumber(this.inboxId)}`,
    }

    const path = routes[this._addNumberType]
    if (path) {
      uiStore.changeRoute({ path })
    }
  }

  redirectToNumbers = () => {
    uiStore.changeRoute({
      path: InboxesPath.inboxNumbers(this.inboxId),
    })
  }

  dispose = () => {
    this._addNumberType = 'assign'
  }
}
